import { Typography } from 'antd';
import { metalCategoryLabelKey, type MetalCategory } from '../../domain/metals/metalCategories';
import {
    formatStockNumber,
    GRAMS_DIGITS,
    MONEY_DIGITS,
    type StockMetalShare,
} from '../../domain/metals/stockSummary';

const { Text } = Typography;

export interface StockMetalBarProps {
    t: (key: string) => string;
    shares: StockMetalShare[];
}

const metalName = (t: StockMetalBarProps['t'], share: StockMetalShare): string =>
    t(metalCategoryLabelKey(share.metalType as MetalCategory));

const ronUnit = 'metals.stockSummary.unit.ron';

/**
 * The stock's worth split by metal as one stacked bar, a color per metal, with a legend that gives
 * each metal's quantity, worth and share under it.
 */
const StockMetalBar = ({ t, shares }: StockMetalBarProps) => (
    <div data-testid="stock-bar" style={{ display: 'grid', gap: 4 }}>
        <div
            style={{
                display: 'flex',
                height: 10,
                borderRadius: 5,
                overflow: 'hidden',
                background: '#d9d9d9',
            }}
        >
            {shares.map(share => (
                <div
                    key={share.metalType}
                    data-testid={`stock-bar-segment-${share.metalType}`}
                    title={`${metalName(t, share)}: ${formatStockNumber(share.percent, 1)}%`}
                    style={{ width: `${share.percent}%`, background: share.color }}
                />
            ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 20px', fontSize: 12 }}>
            {shares.map(share => (
                <div
                    key={share.metalType}
                    data-testid={`stock-bar-${share.metalType}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                >
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            background: share.color,
                            display: 'inline-block',
                        }}
                    />
                    <Text strong style={{ fontSize: 12 }}>
                        {metalName(t, share)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatStockNumber(share.quantity, GRAMS_DIGITS)}{' '}
                        {t('metals.stockSummary.unit.grams')}
                        {' · '}
                        {formatStockNumber(share.valueRon, MONEY_DIGITS)} {t(ronUnit)}
                        {' · '}
                        {formatStockNumber(share.percent, 1)}%
                    </Text>
                </div>
            ))}
        </div>
    </div>
);

export default StockMetalBar;
